import {
  Row, Typography, Button, Skeleton,
  Col, Tag, List, App, Switch, Modal,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { imageLocations } from "../image/ImageLocations";
import PanelFrameWithImage from "./PanelFrameWithImage";
import { React, useEffect, useState } from "react";
import { useScreenSizeBelow } from "../../utility/hooks/useScreenSize";
import {showApiError} from "../../utility/error/error-handler";
import {getRandomInt, getSessionData, storeSessionData} from "../../utility/global-util";
import { useNavigate } from 'react-router-dom';
import { getDriverRouteById } from '../routes/driverRoutes';
import { getIncidentSummaries } from '../../api/im';
import {getTagColour} from '../../sections/view_incident/incident-util.js'
import {clearBodyLocks} from 'tua-body-scroll-lock';
import DataCard from '../data/DataCard.jsx';
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";

const { Text} = Typography;


const ManageIncidentsPanel = ({vehicle, backgroundType, refreshPage, setRefreshPage}) => {
  let incidentData = getSessionData('incidentData');
  const [currentIncident, setCurrentIncident] = useState(incidentData ? incidentData.currentIncident : null);
  const [incidents, setIncidents] = useState(incidentData ? incidentData.incidents : []);
  const [openIncidents, setOpenIncidents] = useState([]);
  const [displayPopup, setDisplayPopup] = useState(false);
  const [displayPopupCount, setDisplayPopupCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const { notification } = App.useApp();
  
  
  const clickButton = (e) => {
    navigate(getDriverRouteById('record-incident'));
  };

  const Buttons = () => {
    return (
      isOnline
      ?
      <div>
        {incidents.length > 0
        ?
        <Button onClick={(e) => setDisplayPopup(true)}>{t('title.other_incidents')}</Button>
        :
        <></>
        }
        <Button type="primary" loading={!vehicle} onClick={(e) => clickButton(e)}>{t('action.report_incident')}</Button>
      </div>
      :
      <></>
    );
  };

  function viewIncident(id)
  {
    storeSessionData('incidentData', {'currentIncident' : currentIncident,
                                     'incidents' : incidents,
                                     'popupDisplay' : displayPopup })
    setDisplayPopup(false);
    clearBodyLocks();
    navigate('/incident-details/' + id);
  }

  const IncidentCard = (params) => {
    return (
            <DataCard header={"Incident " + params.incident.reference}
                      extra={[<EyeOutlined style={{color: 'white', marginRight: '10px', fontSize: '20px', float: 'right'}} onClick={(e) => viewIncident(params.incident.id)}/>]}
            >
              <Row style={{width: "100%"}}>
                <Col span={8}>{params.incident.incidentDate}</Col>
                <Col span={8}>{params.incident.type || ''}</Col>
                <Col span={8}><Tag color={getTagColour(params.incident.status.key)}>{params.incident.status.description}</Tag></Col>
              </Row>
            </DataCard>
          );
  }

  const OtherIncidentsPanel = ({initialIncidents}) => {
    const [includeComplete, setIncludeComplete] = useState(false);
    const [otherIncidents, setOtherIncidents] = useState(initialIncidents);

    function loadOtherIncidents(includeClosed) {
      getIncidentSummaries(vehicle.vehicleId, includeClosed).then(
        (res) => {
          res = res.filter((e) => {return e?.reference !== currentIncident?.reference});
          setIncludeComplete(includeClosed);
          setOtherIncidents(res);}
          ).catch(
            err => {showApiError(t('title.manage_incidents'), err, notification)}
            )
    }

    return (
             <Modal className="std-portal-popup"
                    open={displayPopup}
                    title={t('title.other_incidents')}
                    closable={true}
                    maskClosable={true}
                    keyboard={true}
                    footer={null}
                    onCancel={() => {storeSessionData('incidentData', null); setDisplayPopup(false)}}
             >
              <Row>
                <Text>{t('field.includeCancelledAndCompleted')}</Text>&nbsp;
                <Switch checked={includeComplete} 
                        checkedChildren={<CheckOutlined />} 
                        unCheckedChildren={<CloseOutlined />} 
                        onChange={(e) => {loadOtherIncidents(e)}}
                />
              </Row>
              <List>
                {
                  otherIncidents.map((incident) => {
                    return <List.Item key={getRandomInt(1, 1000)}><IncidentCard key={incident.id} incident={incident}/></List.Item>
                    })
                }
              </List>
            </Modal>
          );
    }

    const ViewPanel = () => {
      const { t } = useTranslation();
      let displayHeight=130;
      if (useScreenSizeBelow(992)) {
        displayHeight = null;
      }

      return (   
               !currentIncident
               ?   
               <div style={{height:displayHeight, overflow:'auto'}}>
                 <Row><Text>{t('message.no_open_incidents')}</Text></Row>
               </div>
               :   
               <div style={{height:displayHeight, overflow:'auto'}}>
                 <Row>
                   <Col span={22}>
                       <Text>{t('message.current_incident', {0: currentIncident.reference, 1: ''})}<span style={{textWrap: 'nowrap'}}>{currentIncident.incidentDate}</span></Text>
                   </Col>
                   <Col span={2}>
                       {isOnline
                        ? <EyeOutlined style={{marginRight: '10px', fontSize: '20px', float: 'right'}} onClick={(e) => viewIncident(currentIncident.id)}/>
                        : <></>}
                   </Col>
                 </Row>
               </div>
             );
    }

    useEffect(() => {
      //TODO Add panel display code here and other dependancies as required. The refreshPage dependancy will reload this information. 
      //See CaptureOdoPanel for more details.
      if (vehicle)
        {
          setCurrentIncident(null);
          setIncidents([]);
          setOpenIncidents([]);
          setLoadingProgress(0);
          
          getIncidentSummaries(vehicle.vehicleId, true).then(
            (res) => {
              if (res.length > 0)
              {
                  const openIncidents = res?.filter(incident => incident?.status.id !== 'COMPLETE' && incident?.status.id !== 'CANCELLED');
                  let openIncidentsSorted = openIncidents.sort((a, b) => a - b);
                  const latestOpenIncident = openIncidentsSorted[0];
                  if (latestOpenIncident !== undefined)
                  {
                      openIncidentsSorted = openIncidentsSorted.filter((e) => {return e?.reference !== latestOpenIncident?.reference});
                      //below code was added to facilitate showing the other incidents button ONLY if there is somethign to show minus the latest open incident
                      res = res.filter((e) => {return e?.reference !== latestOpenIncident?.reference});
                  }

                  setCurrentIncident(latestOpenIncident);
                  setIncidents(res);  //For Other Incidents button control
                  setOpenIncidents(openIncidentsSorted);
              }
              setLoadingProgress(100);}
              ).catch(
                err => {setLoadingProgress(100);showApiError(t('title.manage_incidents'), err, notification)}
                )
        }
        setRefreshPage(false);
    }, [refreshPage, setRefreshPage, notification, t, vehicle]);

    // Display popup if viewing incidents
    useEffect(() => {
      setTimeout(() => {
        if (incidentData && incidentData.popupDisplay && displayPopupCount < 1) 
        {
            setDisplayPopupCount(1);
            setDisplayPopup(incidentData.popupDisplay);
        }
      }, 3000);
    }, [incidentData, incidentData?.popupDisplay, displayPopupCount]);

return (
         <PanelFrameWithImage title={t('title.manage_incidents')}
                              imageLocation={imageLocations.INCIDENT}
                              backgroundType={backgroundType} height="260px"
                              buttons={<Buttons />}
         >
          {vehicle && loadingProgress === 100 //basing on just main object and not having 'loadingProgress === 100' as jumpy at end of load when goes to 0
          ? // Show/Submit Travel Requests
          <div>
              <ViewPanel />
              <OtherIncidentsPanel initialIncidents={openIncidents}/>
          </div>
          : // Loading
          <div>
              <Skeleton active title={false} paragraph={{ rows: 4 }} />
          </div>}
         </PanelFrameWithImage>
       );
}

export default ManageIncidentsPanel;